import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Sort, SortDirection } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { EnergyDataStoreService } from '@app/shared/services/store/energy-data-store.service';
import { MeterAlarm } from '@app/energy-guard/models/alarms';
import { ViewMeterAlarmsExtendedInfo1DtoWithCountTrue } from '@app/energy-guard/models/ViewMeterAlarmsExtendedInfo1';
import { AlarmsService } from '@app/energy-guard/services/alarms.service';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { MeterDataInfoDialogComponent } from '@app/shared/dialog/meter-data-info-dialog/meter-data-info-dialog.component';
import { FilterOptions, MeterViewFilterOption } from '@app/shared/models/meter-data/meter-operator-filter';
import { MeterView, MeterViewResponse } from '@app/shared/models/meter-data/meter-view';
import { consumerKomKeys, consumptionWizardFilters, producerKomKeys } from '@app/shared/constants';
import { KeyboardEvents } from '@app/shared/enums';
import { ConsumptionWizardFilters } from '@app/shared/models/meter/consumption-wizard-filters';
import { CheckedMeter } from '@app/shared/models/meter/meter-wizard-checked-meter';
import { PageEvent } from '@app/shared/models/page-event';
import { MeterDataService } from '@app/shared/services/api/meter-data.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { StringUtility } from '@app/shared/string-utility';
import { SensorDynamicLoaderService } from '@app/shared/services/sensor-dynamic-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { assign, chunk, concat, flatten, includes, isEmpty, isNil, map, orderBy, some, trim, uniqBy } from 'lodash';
import { BehaviorSubject, from, GroupedObservable, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, groupBy, mergeMap, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { FilterOptionsComponent } from '../filter-options/filter-options.component';

@Component({
  selector: 'app-meter-table',
  templateUrl: './meter-table.component.html',
  styleUrls: ['./meter-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MeterTableComponent implements OnInit, OnDestroy {
  protected ngUnsubscribe: Subject<void> = new Subject<void>();
  @Input() isInMeterWizard = false;
  @Input() checkedMeter: { id: string }[] = [];
  @Input() granularity = '';
  @Input() isCurrent = false;
  @Output() maxrowCount$: EventEmitter<number> = new EventEmitter<number>();
  @Output() checkMeter$: EventEmitter<CheckedMeter> = new EventEmitter<CheckedMeter>();
  @ViewChild('filterOptionsComponent', { static: true })
  filterOptionsComponent: FilterOptionsComponent = {} as FilterOptionsComponent;
  private filterOptions: FilterOptions[] = [];
  public connectionObjectNumber = '';
  public data: MeterView[] = [];
  public energyGuardData: { meterId: MeterAlarm } | null = null;
  public groupedData: MeterView[][] = [];
  public hasDeviceEUI = false;
  public index$: BehaviorSubject<number> = new BehaviorSubject(1);
  public isFilterByBuilding = false;
  public isMunicipalityEnergyGuardLicensed = false;
  public isWizardVisible = false;
  public maxRowCount = 0;
  public pageSize = 7;
  public prevSearchParam?: string;
  public resultsPerPage = ['7', '20', '50', 'Alle'];
  public searchParam = '';
  public searchValue$ = new Subject<string>();
  public sortedData: MeterView[] = [];
  public uiSort: {
    active: string;
    direction: SortDirection;
  } = {
    active: '',
    direction: 'asc',
  };

  constructor(
    public authService: AuthService,
    public overlayLoaderService: OverlayLoaderService,
    public translate: TranslateService,
    public _energyDataStore: EnergyDataStoreService,
    private _matDialog: MatDialog,
    private _route: ActivatedRoute,
    private _meterDataService: MeterDataService,
    private _energyGuardAlarmsService: AlarmsService,
    private _router: Router,
    private _userMunicipalitiesService: UserMunicipalitiesService,
    private _sensorDynamicLoader: SensorDynamicLoaderService
  ) {}

  public ngOnInit(): void {
    this._userMunicipalitiesService.currentMunicipality$
      .pipe(
        tap(() => {
          this.overlayLoaderService.isLoading = true;
          this.overlayLoaderService.ignoreChanges = true;
        }),
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((currentMunicipality: MapStoreMunicipality) => this._energyGuardAlarmsService.getLicensedMetersCount(currentMunicipality.key)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((licensedMeters: number) => {
        this.isMunicipalityEnergyGuardLicensed = licensedMeters !== 0;
        this.isFilterByBuilding = this._getParam('action') === 'filterbybuilding';
        this.connectionObjectNumber = this._getParam('connectionobjectnumber');
        if (this.isInMeterWizard) {
          this.isFilterByBuilding = true;
        }
        this.initializeEnergyDataList();
      });
  }

  private _getParam(param: string): string {
    return this._route.snapshot.queryParamMap.get(param) || '';
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public pageChange(pageEvent: PageEvent) {
    this._energyDataStore.filter.pageIndex = pageEvent.index as number;
    this._energyDataStore.filter.pageSize = Number(pageEvent.pageSize);
    this._getMetersView();
  }

  public onInputChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;
    this.searchValue$.next(inputValue);
  }

  public showMeterDataInfoModal(): void {
    this._matDialog.open(MeterDataInfoDialogComponent, {
      panelClass: ['-md'],
    });
  }

  public filterUsingEnergyType(energyTypeNames: string[]): void {
    this._energyDataStore.filter.marketOperators = this.filterOptionsComponent.getMarketOperatorTypes();
    this._energyDataStore.filter.meterTypes = this.filterOptionsComponent.getSelectedMeterType();
    this._energyDataStore.filter.energyTypes = energyTypeNames;
    this._resetPageIndex();
    this._getMetersView();
  }

  public filterUsingMeterType(meterTypeNames: string[]): void {
    this._energyDataStore.filter.marketOperators = this.filterOptionsComponent.getSelectedMarketOperator();
    this._energyDataStore.filter.meterTypes = meterTypeNames;
    this._energyDataStore.filter.energyTypes = this.filterOptionsComponent.getSelectedEnergyType();
    this._resetPageIndex();
    this._getMetersView();
  }

  public filterUsingMarketOperator(marketOperatorNames: string[]): void {
    this._energyDataStore.filter.marketOperators = marketOperatorNames;
    this._energyDataStore.filter.meterTypes = this.filterOptionsComponent.getSelectedMeterType();
    this._energyDataStore.filter.energyTypes = this.filterOptionsComponent.getSelectedEnergyType();
    this._resetPageIndex();
    this._getMetersView();
  }

  public onSearchKeydown(event: KeyboardEvent) {
    if (StringUtility.areStringsIdentical(event.key, KeyboardEvents.enter) && this.searchParam) {
      this.searchMeter();
    }
  }

  public searchMeter(): void {
    if (this._energyDataStore.filter.searchParam?.trim() === this.searchParam) {
      return;
    }

    if (this.isInMeterWizard) {
      this.offlineSearch();
    } else {
      this._energyDataStore.filter.searchParam = this.searchParam?.trim();
      this._resetPageIndex();
      this._getMetersView();
    }
  }

  private offlineSearch(): void {
    const flattenedGroupedMeters = flatten(this.groupedData);
    const filteredMeter = flattenedGroupedMeters.filter((meter: MeterView) => {
      const searchValue = this.searchParam?.toLowerCase().trim();
      const isInSearchAddress = includes(meter.getAddress().toLowerCase().trim(), searchValue);
      const isInSearchMeterNumbers = includes(meter.meterNumber.toLowerCase().trim(), searchValue);
      const filterMarketKey = isNil(meter.marketLocationKey) ? '' : meter.marketLocationKey.toLowerCase();
      const isInSearchMarketLocationKey = includes(filterMarketKey, searchValue);
      const isInSearchObjectType = includes(meter.objectType.toLowerCase().trim(), searchValue);

      return isInSearchAddress || isInSearchMeterNumbers || isInSearchMarketLocationKey || isInSearchObjectType;
    });

    const filteredGroupData = chunk(filteredMeter, 1);
    this.groupedData = filteredGroupData;
    this.maxRowCount = this.groupedData.length;
    this._energyDataStore.filter.searchParam = this.searchParam.trim();
  }

  private initializeEnergyDataList(): void {
    this.overlayLoaderService.isLoading = true;
    this.searchValue$
      .pipe(
        filter(() => !isNil(this._userMunicipalitiesService.currentMunicipality)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((value: string) => {
        if (this.prevSearchParam !== value) {
          this.prevSearchParam = value.toString();
          if (isNil(value) || isEmpty(value)) {
            this._energyDataStore.filter.searchParam = value;
            this._getMetersView();
          }
        }
      });

    this._meterDataService
      .getViewMeterFilterOptions(this._userMunicipalitiesService.currentMunicipality.key)
      .pipe(
        switchMap(() => this.filterOptionsComponent.loadItems()),
        tap(() => (this.filterOptions = this._meterDataService.meterFilterOptions)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        if (
          !isNil(this._userMunicipalitiesService.currentMunicipality) &&
          trim(this._userMunicipalitiesService.currentMunicipality.key).length > 0 &&
          (this._municipalityHasChanged() || this.isFilterByBuilding)
        ) {
          const tempSort = this._energyDataStore.filter.sort;
          const tempPageSize = this._energyDataStore.filter.pageSize;
          this._energyDataStore.filter = this._energyDataStore.reset();
          this._energyDataStore.filter.sort = tempSort;
          this._energyDataStore.filter.pageSize = tempPageSize;
          this._setLocalFilters();
          this._getMetersView();
        } else {
          this._setLocalFilters();
          this._getMetersView();
        }
      });
  }

  private _generateConnectionObjectKey(connectionObjectKey: string): string {
    return this.isFilterByBuilding ? (connectionObjectKey ? connectionObjectKey : '') : '';
  }

  private _setLocalFilters(): void {
    this._energyDataStore.filter.municipalityKey = this._userMunicipalitiesService.currentMunicipality.key;
    this._energyDataStore.filter.energyTypes = some(this._energyDataStore.filter.energyTypes)
      ? this._energyDataStore.filter.energyTypes
      : this.filterOptionsComponent.getEnergyTypes();
    this._energyDataStore.filter.meterTypes = some(this._energyDataStore.filter.meterTypes)
      ? this._energyDataStore.filter.meterTypes
      : this.filterOptionsComponent.getMeterTypes();
    this._energyDataStore.filter.marketOperators = this.filterOptionsComponent.getMarketOperatorTypes();
    this._energyDataStore.filter.connectionOjectNumber = this._generateConnectionObjectKey(this.connectionObjectNumber);
    this.searchParam = this._energyDataStore.filter.searchParam;
    this.prevSearchParam = this.searchParam;
    this.pageSize = this._energyDataStore.filter.pageSize;
    this.index$.next(this._energyDataStore.filter.pageIndex);
    this.maxRowCount = this._energyDataStore.filter.maxRowCount;
    this.filterOptionsComponent.setSelected();
  }

  private _municipalityHasChanged(): boolean {
    return this._energyDataStore.filter.municipalityKey !== this._userMunicipalitiesService.currentMunicipality.key;
  }

  private _resetPageIndex(): void {
    this.maxRowCount = 0;
    this.index$.next(1);
    this._energyDataStore.filter.pageIndex = 1;
    this.pageSize = this._energyDataStore.filter.pageSize;
  }

  private _getMetersView(): void {
    this.overlayLoaderService.isLoading = true;
    this.groupedData = [];
    this.data = [];
    this.energyGuardData = null;
    let checkedMeterNumbers: string[] = [];

    this._meterDataService
      .getMeterViewByMunicipality(
        this._energyDataStore.filter.municipalityKey,
        this._energyDataStore.filter.searchParam,
        this._energyDataStore.filter.energyTypes,
        this._energyDataStore.filter.meterTypes,
        this._energyDataStore.filter.marketOperators,
        this.isFilterByBuilding ? 0 : this._energyDataStore.filter.pageIndex - 1,
        this.isFilterByBuilding ? 0 : this._energyDataStore.filter.pageSize,
        this._energyDataStore.filter.sort,
        false,
        this._energyDataStore.filter.serialNumbers,
        '',
        this._energyDataStore.filter.connectionOjectNumber,
        true
      )
      .pipe(
        catchError(() => of({} as MeterViewResponse)),
        switchMap((resp: MeterViewResponse) => {
          if (isEmpty(resp)) {
            return of({ data: [], maxRowCount: 0 });
          }

          let meterNumbers: string[] = [];
          let connectionNumber = '';
          if (this.isInMeterWizard && !this.isCurrent) {
            meterNumbers = [];
          } else {
            meterNumbers = map(resp.data, (item: MeterView) => {
              return item.meterNumber;
            });
            connectionNumber = this._energyDataStore.filter.connectionOjectNumber;
          }

          if (resp.data.length === 1) {
            checkedMeterNumbers = map(resp.data, (item: MeterView) => {
              return item.meterNumber;
            });
          }

          this.maxRowCount = resp.maxRowCount;
          this.maxrowCount$.emit(this.maxRowCount);
          this._energyDataStore.filter.maxRowCount = this.maxRowCount;

          return this._meterDataService.getMeterViewByMunicipality(
            this._energyDataStore.filter.municipalityKey,
            this.searchParam ? this._energyDataStore.filter.searchParam : '',
            this._energyDataStore.filter.energyTypes,
            this._energyDataStore.filter.meterTypes,
            this._energyDataStore.filter.marketOperators,
            0,
            0,
            this._energyDataStore.filter.sort,
            false,
            this._energyDataStore.filter.pageSize === 0 ? [] : meterNumbers,
            '',
            connectionNumber,
            false
          );
        })
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: MeterViewResponse) => {
          if (!response?.data?.length) {
            this.maxRowCount = 0;
            return;
          }

          const mappedResponse: MeterView[] = this.addFilterOptions(response.data);
          this.data = map(mappedResponse, (item: MeterView) => {
            return assign(new MeterView(), item);
          });

          this.hasDeviceEUI = this._deviceCount(this.data);
          this.sortedData = this.data;
          if (this._energyDataStore.filter.pageSize !== 0) {
            this.sortedData = this.sortedData.slice(0, this._energyDataStore.filter.pageSize);
          }

          const ungroupedData: Observable<MeterView> = from(this.sortedData);

          if (this.isInMeterWizard) {
            const meterFilters = consumptionWizardFilters.filter((wizardFilter: ConsumptionWizardFilters) =>
              StringUtility.areStringsIdentical(wizardFilter.timePeriod, this.granularity)
            );

            let data: MeterView[][] = [];

            meterFilters.forEach((wizardFilter: ConsumptionWizardFilters) => {
              const tempArray = this.data.filter(
                (meter: MeterView) =>
                  StringUtility.areStringsIdentical(meter.meterTypeCode, wizardFilter.meterType) &&
                  (isNil(wizardFilter.energyTypesName) || StringUtility.areStringsIdentical(meter.energyTypesName, wizardFilter.energyTypesName)) &&
                  (isNil(wizardFilter.hasDeviceEui) || (wizardFilter.hasDeviceEui ? !isNil(meter.deviceId) : isNil(meter.deviceId)))
              );

              if (tempArray.length > 0) {
                data = concat(data, chunk(tempArray, 1));
              }
            });

            this.groupedData = uniqBy(data, (meter: MeterView[]) => meter[0].meterNumber);
            this.maxRowCount = this.groupedData.length;

            if (!this.isCurrent) {
              this.groupedData.forEach((meter: MeterView[]) => {
                const obj = checkedMeterNumbers.find((checkedMeterNumber: string) =>
                  StringUtility.areStringsIdentical(checkedMeterNumber, meter[0].meterNumber)
                );
                if (!isNil(obj)) {
                  this.checkMeter$.emit({
                    address: meter[0].getAddress().replace('\r\n', ' '),
                    checked: true,
                    converterFactor: meter[0].converterFactor,
                    deviceId: meter[0].deviceId,
                    energyTypesName: meter[0].energyTypesName,
                    id: meter[0].guid,
                    marketLocationKey: meter[0].marketLocationKey,
                    marketOperatorCategory: meter[0].getMarketOperatorCategory(),
                    meterNumber: meter[0].meterNumber,
                    meterOperator: meter[0].getMarketOperatorText(),
                    meterTypeCode: meter[0].meterTypeCode,
                    meterTypesName: meter[0].meterTypesName,
                  } as CheckedMeter);
                }
              });
            }

            if (this.groupedData.length === 1) {
              this.groupedData.forEach((meter: MeterView[]) => {
                this.checkMeter$.emit({
                  address: meter[0].getAddress().replace('\r\n', ' '),
                  checked: true,
                  converterFactor: meter[0].converterFactor,
                  deviceId: meter[0].deviceId,
                  energyTypesName: meter[0].energyTypesName,
                  id: meter[0].guid,
                  marketLocationKey: meter[0].marketLocationKey,
                  marketOperatorCategory: meter[0].getMarketOperatorCategory(),
                  meterNumber: meter[0].meterNumber,
                  meterOperator: meter[0].getMarketOperatorText(),
                  meterTypeCode: meter[0].meterTypeCode,
                  meterTypesName: meter[0].meterTypesName,
                } as CheckedMeter);
              });
            }
          } else {
            if (this._energyDataStore.filter.sort === '') {
              const g: Observable<MeterView[]> = ungroupedData.pipe(
                groupBy((data: MeterView) => data.meterNumber),
                mergeMap((group: GroupedObservable<string, MeterView>) => group.pipe(toArray()))
              );

              g.pipe(takeUntil(this.ngUnsubscribe)).subscribe((grouped: MeterView[]) => {
                let distinct: MeterView[] = grouped;
                distinct = map(distinct, (distinctUnsortedMeter: MeterView) => {
                  const komKeys = grouped
                    .filter(
                      (meter: MeterView) =>
                        StringUtility.areStringsIdentical(meter.meterNumber, distinctUnsortedMeter.meterNumber) &&
                        StringUtility.areStringsIdentical(meter.marketLocationKey, distinctUnsortedMeter.marketLocationKey)
                    )
                    .map((meter: MeterView) => meter.komKey);
                  return assign(distinctUnsortedMeter, {
                    komKeyArray: komKeys,
                  });
                });

                if (distinct.length > 1) {
                  if (!isEmpty(distinct.filter((meter: MeterView) => consumerKomKeys.includes(meter.komKey)))) {
                    let distinctConsumer = distinct.filter((meter: MeterView) => consumerKomKeys.includes(meter.komKey));
                    distinctConsumer = orderBy(distinctConsumer, [(consumerMeter: MeterView) => consumerMeter.objectType || null], ['asc']);
                    distinctConsumer.forEach((distinctWithConsumerKomKeys: MeterView) => {
                      this.groupedData.push([distinctWithConsumerKomKeys]);
                    });
                  }

                  if (!isEmpty(distinct.filter((meter: MeterView) => producerKomKeys.includes(meter.komKey)))) {
                    let distinctProducer = distinct.filter((meter: MeterView) => producerKomKeys.includes(meter.komKey));
                    distinctProducer = orderBy(distinctProducer, [(producerMeter: MeterView) => producerMeter.objectType || null], ['asc']);
                    distinctProducer.forEach((distinctWithProducerKomKeys: MeterView) => {
                      this.groupedData.push([distinctWithProducerKomKeys]);
                    });
                  }
                } else {
                  this.groupedData.push(distinct);
                }
              });
            } else {
              this.sortedData.forEach((distinctSortedMeter: MeterView) => {
                const komKeys = this.sortedData
                  .filter(
                    (meter: MeterView) =>
                      StringUtility.areStringsIdentical(meter.meterNumber, distinctSortedMeter.meterNumber) &&
                      StringUtility.areStringsIdentical(meter.marketLocationKey, distinctSortedMeter.marketLocationKey)
                  )
                  .map((meter: MeterView) => meter.komKey);
                assign(distinctSortedMeter, { komKeyArray: komKeys });
                this.groupedData.push([distinctSortedMeter]);
              });
            }

            if (this.isFilterByBuilding && this.groupedData.length === 1) {
              this._navigateToConsumptionPage(this.groupedData[0][0]);
            }
          }

          if (!!this.authService.permissions.ENERGY_GUARD && this.isMunicipalityEnergyGuardLicensed) {
            this._getEnergyGuardMeterStatus(this.data.map((meter: MeterView) => meter.guid));
          }
        },
        error: () => this.overlayLoaderService.setLoadingToFalse(),
        complete: () => this.overlayLoaderService.setLoadingToFalse(),
      });
  }

  private _deviceCount(data: MeterView[] = []): boolean {
    return some(data, (meter: MeterView) => !isNil(meter.deviceId));
  }

  private addFilterOptions(meterData: MeterView[]): MeterView[] {
    return meterData.map((data: MeterView) => {
      return {
        ...data,
        filterOptions: this.filterOptions.map((option: FilterOptions) => {
          return {
            filterName: option.name,
            filterValue: option.msbid,
            lineOfBusiness: option.lineOfBusiness,
            msbCategory: option.msbCategory,
          };
        }) as MeterViewFilterOption[],
      } as MeterView;
    });
  }

  private _navigateToConsumptionPage(meterView: MeterView): void {
    this._router.navigate(['/verbrauch'], {
      queryParams: {
        connectionobjectnumber: meterView.connectionObjectNumber,
        guid: meterView.guid,
        reroute: true,
      },
      queryParamsHandling: 'merge',
    });
  }

  public sortData(sort: Sort): void {
    const sorts = sort.active.split(',');
    this._energyDataStore.filter.sort = `${sorts[0]} ${sort.direction}`;
    this._getMetersView();
  }

  private _getEnergyGuardMeterStatus(meterIds: string[]): void {
    this._energyGuardAlarmsService
      .getMetersAlarms(this._energyDataStore.filter.municipalityKey, '', this._energyDataStore.filter.pageSize > 0 ? meterIds : '')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: ViewMeterAlarmsExtendedInfo1DtoWithCountTrue) => {
        const alarmsData: MeterAlarm[] = response.data;
        meterIds.forEach((meterId: string) => {
          const alarmData = alarmsData.find((meterAlarm: MeterAlarm) => StringUtility.areStringsIdentical(meterAlarm.guid, meterId));
          this.energyGuardData = assign(this.energyGuardData, {
            [meterId]: alarmData,
          });
        });
      });
  }

  public async onboardElement(): Promise<void> {
    const onboarded = await this._sensorDynamicLoader.openSensorOnboardingDialog();
    if (onboarded) {
      this._getMetersView();
    }
  }

  public onCheckMeter($event: CheckedMeter): void {
    this.checkMeter$.emit($event);
  }

  public isInCheckMeterList(meter: MeterView[]): boolean {
    const hasMeter = this.checkedMeter.find((checked: { id: string }) => StringUtility.areStringsIdentical(checked.id, meter[0].guid));
    return hasMeter ? this.checkedMeter.indexOf(hasMeter) !== -1 : false;
  }

  public onMeterCheckAllChange($event: MatCheckboxChange): void {
    if ($event) {
      this.groupedData.forEach((meter: MeterView[]) => {
        const obj = this.checkedMeter.find((checked: { id: string }) => StringUtility.areStringsIdentical(checked.id, meter[0].guid));
        if (!isNil(obj)) {
          this.checkMeter$.emit({
            address: meter[0].getAddress().replace('\r\n', ' '),
            checked: $event.checked,
            converterFactor: meter[0].converterFactor,
            deviceId: meter[0].deviceId,
            energyTypesName: meter[0].energyTypesName,
            id: meter[0].guid,
            marketLocationKey: meter[0].marketLocationKey,
            marketOperatorCategory: meter[0].getMarketOperatorCategory(),
            meterNumber: meter[0].meterNumber,
            meterOperator: meter[0].getMarketOperatorText(),
            meterTypeCode: meter[0].meterTypeCode,
            meterTypesName: meter[0].meterTypesName,
          } as CheckedMeter);
        }

        this.checkMeter$.emit({
          address: meter[0].getAddress().replace('\r\n', ' '),
          checked: $event.checked,
          converterFactor: meter[0].converterFactor,
          deviceId: meter[0].deviceId,
          energyTypesName: meter[0].energyTypesName,
          id: meter[0].guid,
          marketLocationKey: meter[0].marketLocationKey,
          marketOperatorCategory: meter[0].getMarketOperatorCategory(),
          meterNumber: meter[0].meterNumber,
          meterOperator: meter[0].getMarketOperatorText(),
          meterTypeCode: meter[0].meterTypeCode,
          meterTypesName: meter[0].meterTypesName,
        } as CheckedMeter);
      });
    }

    if (this.searchParam?.toLowerCase().trim() !== '') {
      this.offlineSearch();
    }
  }

  public get isAllChecked(): boolean {
    const result = this.groupedData.filter((meter: MeterView[]) => {
      const guid = meter[0].guid;
      return some(this.checkedMeter, (checked: { id: string }) => StringUtility.areStringsIdentical(checked.id, guid));
    });
    return result.length === this.groupedData.length;
  }
}
